import React, { useEffect, useState } from 'react';
import '../scss/widget.scss';
import WidgetModel from '../models/WidgetModel.js';
import ListWidget from './templates/ListWidget.js';
import CardWidget from './templates/CardWidget.js';
import RelatedWidget from './templates/RelatedWidget.js';
import DFEWidget from './templates/DFEWidget.js';
import utils from '../../common/utils.js';
import cache from '../services/cache.js';

const CONTEXT_DISPLAY = 'display';
const CONTEXT_EDITOR = 'editor';
const WIDGET_MODE = 'standard';
const STATIC_URL = '';

const TEMPLATE_LIST = 'list';
const TEMPLATE_CARD = 'card';
const TEMPLATE_RELATED = 'related';
const TEMPLATE_DFE = 'dfe';

const STATUS_LOADING = 'loading';
const STATUS_EMPTY = 'empty';
const STATUS_LOADED = 'loaded';

export default (props) => {

	const options = props.options;
	// context is only set in React components
	const context = props.context || CONTEXT_DISPLAY;
	const inEditor = CONTEXT_EDITOR === context;

	// special component flags
	// don't display on page (for editor counter widget - does query, no display)
	const hidden = utils.toBoolean(props.hidden);

	const [status, setStatus] = useState(STATUS_LOADING);
	const [widgetModel, setWidgetModel] = useState(null);
	const [posts, setPosts] = useState([]);
	const [queryId, setQueryId] = useState(null);
	const [page, setPage] = useState(1);
	const [pages, setPages] = useState(1);
	const [allTotal, setAllTotal] = useState(0);
	const [total, setTotal] = useState(0);
	const [filters, setFilters] = useState([]);
	const [searchQuery, setSearchQuery] = useState('');
	const [template, setTemplate] = useState('');
	const [filterValues, setFilterValues] = useState({});

	const getPosts = () => {
		cache.queryCount = utils.toNumber(cache.queryCount || 0) + 1;
		setQueryId(new Date().getTime());
	};

	// convert filter text values to valid query params
	const getQueryParamValue = (text) => utils.cleanString(text)
		.toLowerCase()
		.replace(/^cdc\s/,'')
		.replace(/[\?\;\:\^\&\'\"\.\,]+/g,'')
		.replace(/[\-\s]+/g,'-')
		.replace(/\-\-+/g,'-')
		.trim();

	// bundled object to share with child template components
	const data = {
		queryId,
		posts,
		page,
		pages,
		allTotal,
		total,
		filters,
		filterValues,
		searchQuery,
		setFilterValues,
		setSearchQuery,
		setPage,
		getPosts,
		widgetModel,
		inEditor,
	};

	// on load
	useEffect(() => {
		// prop options are settings that can't be set in HTML attributes
		let widgetOptions = Object.assign({},options);
		widgetOptions.inEditor = inEditor;
		widgetOptions.context = context;
		widgetOptions.hidden = hidden;
		// these are settings that defer to props, then HTML data attributes
		widgetOptions.mode = props.mode || options.mode || WIDGET_MODE;
		widgetOptions.static = props.static || options.static || STATIC_URL;
		// optionally allow overriding template
		if (props.template || options.template) {
			widgetOptions.template = props.template || options.template;
		}
		// option to hide all form elements, if configured (search, filters)
		widgetOptions.hideForm = utils.toggleSwitch(props.hideForm || options.hasOwnProperty('hide-form'));

		// option to just hide filters, if configured
		widgetOptions.hideFilters = utils.toggleSwitch(props.hideFilters || options.hasOwnProperty('hide-filters')) || widgetOptions.hideForm;

		// option to hide images
		widgetOptions.hideImages = utils.toggleSwitch(props.hideImages || options.hasOwnProperty('hide-images'));

		// option to hide dates
		widgetOptions.hideDates = utils.toggleSwitch(props.hideDates || options.hasOwnProperty('hide-dates'));

		// option to hide descriptions
		widgetOptions.hideContent = utils.toggleSwitch(props.hideContent || props.hideDescriptions || options.hasOwnProperty('hide-content') || options.hasOwnProperty('hide-descriptions'));

		// option to hide alt formats and languages
		widgetOptions.hideAlts = utils.toggleSwitch(props.hideAlts || options.hasOwnProperty('hide-alts'));

		// option to hide pagination
		widgetOptions.hidePagination = utils.toggleSwitch(props.hidePagination || options.hasOwnProperty('hide-pagination'));

		// optional limit set in widget
		widgetOptions.limit = utils.toNumber(props.limit || options.limit || 0);

		// optional content truncate
		widgetOptions.limitContent = utils.toNumber(props.limitContent || options['limit-content'] || 0);

		// optional title truncate
		widgetOptions.limitTitle = utils.toNumber(props.limitTitle || options['limit-title'] || 0);

		setWidgetModel(new WidgetModel(widgetOptions));
	}, [options.options]);

	useEffect(() => {
		if (!widgetModel) {
			return;
		}
		// on first load set widget status
		// - posts found, loaded
		// - no posts, empty
		widgetModel.whenReady().then(() => {
			setTemplate(widgetModel.template);
			if (widgetModel.filters.length) {
				let currentFilterValues = {};
				widgetModel.filters.forEach((filter) => {
					currentFilterValues[filter.id] = null;
				});
			}
			getPosts();
		}).catch(error => {
			console.error('cc: cannot load config: ', error);
		});
	}, [widgetModel]);

	useEffect(() => {
		if (STATUS_LOADED !== status) {
			return;
		}
	}, [filterValues, page]);

	// CHECK QUERY PARAMETERS
	// Once filter values are grabbed, see if any are passed via query params match filters
	useEffect(() => {
		if(!filters||!filters.length) {
			return;
		}
		let newFilterValues = {};
		let doQuery = false;
		filters.forEach(filter => {
			const filterId = filter.id;
			let filterName = getQueryParamValue(filter.label || filter.name);
			let filterValue = utils.getParam(filterName);
			if (!filterValue || !filterName || !filter.options || !filter.options.length) {
				return;
			}
			filterValue = getQueryParamValue(filterValue);
			for(let optionIndex in filter.options) {
				let option = filter.options[optionIndex];
				let optionValue = getQueryParamValue(option.label || option.name || option.value);
				if (optionValue === filterValue) {
					newFilterValues[filterId] = option;
					doQuery = true;
					break;
				}
			}
		});
		// if we have a match, do a query on the filtered terms
		if (doQuery) {
			setFilterValues(newFilterValues);
			getPosts();
		}
	}, [filters]);

	// trigger for query posts
	useEffect(() => {
		if (!queryId) {
			return;
		}
		const query = {
			search: utils.cleanString(searchQuery),
			filters: filterValues,
			page,
		};
		widgetModel.getPosts(query).then((response) => {
			// Filters come back from first response, as part of response
			if (!filters.length && response.filters) {
				setFilters(response.filters);
			}
			setPosts(response.posts);
			if (response.page !== page) {
				setPage(utils.toNumber(response.page));
			}
			// set once
			if (!allTotal) {
				setAllTotal(response.total);
			}
			setTotal(response.total);
			setPages(utils.toNumber(response.pages));

			if (STATUS_LOADING === status) {
				setStatus(STATUS_LOADED);
				// @TODO: Allow empty on no results
				if (!response?.posts?.length) {
					setStatus(STATUS_EMPTY);
				}
			}
		});
	}, [queryId]);

	// when in the editor, the widget can load many times
	// this is when options are passed via options={options: WidgetOptions}
	useEffect(() => {
		if (!inEditor || !widgetModel || !widgetModel.ready || !options.options) {
			return;
		}
		widgetModel.setOptions(options.options);
		widgetModel.getPosts();
	}, [options.options])

	useEffect(() => {
		let qp = [];
		for (let filterId in filterValues) {
			let filter = filters.find((filterItem) => (filterItem.id === filterId));
			let filterOption = filterValues[filterId];
			if (!filter || !filterOption) {
				return;
			}
			let options = utils.toArray(filterOption);
			options.forEach(option => {
				qp.push(getQueryParamValue(filter.label || filter.name) + '=' + getQueryParamValue(option.label || option.name || option.value));
			})
		}
		if (qp.length && widgetModel?.debug) {
			// output in console query string to use
			console.info( 'cc: Filter Query string: ', '?' + qp.join('&') );
		}

	}, [filterValues])

	// there's a no-display in editor mode
	if (inEditor && hidden) {
		return '';
	}

	// still loading
	if (!widgetModel?.ready || STATUS_LOADING === status) {
		return <div className="cdc-cc-widget cdc-cc-widget__loading" data-status={status}></div>;
	}

	// loaded, but empty
	if (STATUS_EMPTY === status) {
		if (inEditor) {
			return (
				<div className="cdc-cc-widget cdc-cc-widget__empty" data-status={status}>
					<p>No posts returned for current selection criteria. Check your criteria and target posts.</p>
				</div>
			);
		}
		return <div className="cdc-cc-widget cdc-cc-widget__empty" data-status={status}>
			{widgetModel?.debug ? (
				<small>
					{widgetModel.id} collection found no posts.
				</small>
			) : ''}
		</div>;
	}

	switch (template) {
		case TEMPLATE_CARD:
			return <CardWidget data={data}></CardWidget>;
		case TEMPLATE_RELATED:
			return <RelatedWidget data={data}></RelatedWidget>;
		case TEMPLATE_DFE:
			return <DFEWidget data={data}></DFEWidget>
		case TEMPLATE_LIST:
		default:
			return <ListWidget data={data}></ListWidget>;
	}
};
